import { useCallback, useMemo, useState } from 'react';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, StepProps, useAlert } from '@frontend/design-system';
import { useAddCardOnFileJustifi } from '../../../../hooks/justifi/use-add-card-on-file-justifi';
import { useCardOnFilePaymentJustifi } from '../../../../hooks/justifi/use-card-on-file-payment-justifi';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useUpdateRequestStatusPoll } from '../../../hooks/use-update-request-status-poll';
import { useFinalizeStepTrackingCallbacks, useInitiateStepTracking } from '../../../utils/step-tracking-store';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CardOnFileJustifiUI, CardOnFileJustifiViews } from './card-on-file-justifi/card-on-file-justifi-ui';
import { AddCardForm } from './card-on-file-justifi/views/add-card-form';
import { CardOnFileNavigationSwitch } from './card-on-file-navigation-switch';
import { CardOnFileStepBodyProps } from './card-on-file-types';

export const CardOnFileJustifi = () => {
  return <CardOnFileNavigationSwitch body={CardOnFileJustifiContent} />;
};
const CardOnFileJustifiContent = ({ onPaymentSuccess, onCompleteStep, onGoBack }: CardOnFileStepBodyProps) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const [view, setView] = useState<CardOnFileJustifiViews>('payment-methods-list');

  const [selectedPM, setSelectedPM] = useState<PaymentMethod>();
  const {
    activeLocationId,
    person,
    invoice,
    flow,
    invoiceQuery: { refetch: refetchInvoice },
    activeGateway,
  } = useCollectPaymentMultiContext();

  const onChangeView = (view: CardOnFileJustifiViews) => {
    setView(view);
  };

  const {
    shouldShowLoading,
    primary: addPrimaryBtnProps,
    secondary: addCancelBtnProps,
    onSubmit: onSubmitCardForm,
    resetFormState,
    justifiProps,
    nameFieldProps,
    elementRef,
  } = useAddCardOnFileJustifi({
    locationId: activeLocationId || '',
    personId: person?.personId,
    onError: (error) => {
      alert.error(error || 'An error occurred while processing your request.');
    },
    onCancel: () => {
      setView('payment-methods-list');
    },
    onCardSaved: (pm: PaymentMethod) => {
      setSelectedPM(pm);
      setView('payment-methods-list');
    },
  });

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoice().then((res) => res.data);
  }, [refetchInvoice]);

  const { makeCardOnFilePayment, makingCardOnFilePayment } = useCardOnFilePaymentJustifi({
    paymentMethod: selectedPM,
    invoice,
    onInvoiceAlreadyPaid: onGoBack,
    onRefetchInvoice,
  });

  const { setProcessingAndStartPoll } = useUpdateRequestStatusPoll(invoice, () => {
    onPaymentSuccess();
  });

  const { completeStepTracking, cancelStepTracking } = useFinalizeStepTrackingCallbacks({
    step: 'card-on-file',
    variant: 'card-on-file:complete-payment',
  });

  const makePayment = useCallback(async () => {
    const { workflowId } = await makeCardOnFilePayment();

    /**
     * If card is declided, or failure happens, workflowId will be undefined.
     */
    if (workflowId) {
      alert.success(t('Payment submitted successfully!'));

      setProcessingAndStartPoll();

      onCompleteStep();

      completeStepTracking();
    }
  }, [makeCardOnFilePayment, onCompleteStep, setProcessingAndStartPoll, completeStepTracking]);

  const onClickPrimary = useCallback(async () => {
    await makePayment();
  }, [makePayment]);

  const onClickBack = useCallback(() => {
    onGoBack();
    cancelStepTracking();
  }, [onGoBack, cancelStepTracking]);

  const { backTrackingId, completePaymentTrackingId } = useScopedTrackingIds({
    backTrackingId: `${PAYMENT_MULTI_STEPS.cardOnFile}--${activeGateway}--back-btn`,
    completePaymentTrackingId: `${PAYMENT_MULTI_STEPS.cardOnFile}--${activeGateway}--primary-btn`,
  });

  const footerProps = useMemo<StepProps['footer']>(() => {
    if (view === 'payment-methods-list') {
      return {
        primary: {
          children: makingCardOnFilePayment ? t('Completing Payment') : t('Complete Payment'),
          disabled: !selectedPM || makingCardOnFilePayment,
          trackingId: completePaymentTrackingId,
        },
        back: {
          children: t('Back'),
          trackingId: backTrackingId,
        },
        onClickPrimary,
        onClickBack,
      };
    }

    return {
      primary: addPrimaryBtnProps,
      back: addCancelBtnProps,
      onClickPrimary: onSubmitCardForm,
      onClickBack: () => {
        setView('payment-methods-list');
      },
    };
  }, [
    view,
    selectedPM,
    onClickPrimary,
    onClickBack,
    onSubmitCardForm,
    makingCardOnFilePayment,
    addPrimaryBtnProps,
    addCancelBtnProps,
    t,
  ]);

  const heading = useMemo(() => {
    if (view === 'add-card-form') {
      return t('Card on File');
    }

    return PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.cardOnFile];
  }, [view]);

  useInitiateStepTracking({
    activeGateway: 'justifi',
    flow,
    step: 'card-on-file',
    screenWidth: window.innerWidth,
    variant: 'card-on-file:complete-payment',
  });

  return (
    <StyledStep id={PAYMENT_MULTI_STEPS.cardOnFile} label={heading} footer={footerProps}>
      <ContentLoader show={makingCardOnFilePayment} message={t('Making Card on File Payment...')} />

      <CardOnFileJustifiUI
        personId={person?.personId}
        locationId={activeLocationId}
        selectedPM={selectedPM}
        view={view}
        onChangeView={onChangeView}
        onSelectPaymentMethod={setSelectedPM}
        addCardFormContent={
          <AddCardForm
            justifiProps={justifiProps}
            nameFieldProps={nameFieldProps}
            elementRef={elementRef}
            shouldShowLoading={shouldShowLoading}
            resetFormState={resetFormState}
          />
        }
      />
    </StyledStep>
  );
};
